import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
  Chip,
  Skeleton,
  Tooltip,
} from '@mui/material';
import { Appointment, ShopSettings } from '../../../types';
import { format, addDays } from 'date-fns';
import { Add } from '@mui/icons-material';
import { DEFAULT_TIME_SLOTS, formatTimeForDisplay } from './helpers';
import { Shop, SpecialHours } from '../../../types';

interface TimeSlotsTabProps {
  /** The shops staff can book for, in the order to list them. */
  shopIds: string[];
  shops: Shop[];
  availableTimeSlots: string[];
  slotBookingCounts: Record<string, number>;
  shopSettings: ShopSettings | null;
  selectedDate: Date;
  selectedShopForTimeSlots: string;
  setSelectedShopForTimeSlots: (shopId: string) => void;
  shopAppointments: Appointment[];
  shopBookedSlots: string[];
  isLoadingShopSlots: boolean;
  loadingSettings: boolean;
  isDateAvailable: (date: Date) => boolean;
  isHoliday: (date: Date) => boolean;
  isClosedDay: (date: Date) => boolean;
  getSpecialHours: (date: Date) => SpecialHours | undefined;
  handleDateChange: (date: Date | null) => void;
  /** Opens a new booking, at `timeSlot` when a slot was clicked. */
  handleOpenNewBookingDialog: (timeSlot?: string) => void;
  handleRescheduleClick: (appointment: Appointment) => void;
  formatAppointmentDateTime: (appointment: Appointment) => string;
}

/** The "Available Time Slots" tab: pick a shop and a day, see how full each slot is. */
const TimeSlotsTab: React.FC<TimeSlotsTabProps> = ({
  shopIds,
  shops,
  availableTimeSlots,
  slotBookingCounts,
  shopSettings,
  selectedDate,
  selectedShopForTimeSlots,
  setSelectedShopForTimeSlots,
  shopAppointments,
  shopBookedSlots,
  isLoadingShopSlots,
  loadingSettings,
  isDateAvailable,
  isHoliday,
  isClosedDay,
  getSpecialHours,
  handleDateChange,
  handleOpenNewBookingDialog,
  handleRescheduleClick,
  formatAppointmentDateTime,
}) => {
  console.log('Current slot booking counts:', slotBookingCounts);
  console.log('Selected shop for time slots:', selectedShopForTimeSlots);
  console.log('Selected date:', selectedDate);
  console.log('Shop settings:', shopSettings);
  
  // Ensure we're using the correct time slot size
  if (shopSettings?.timeSlotSize) {
    console.log(`Using shop time slot size: ${shopSettings.timeSlotSize} minutes`);
  } else {
    console.log('No shop settings found, using default 30-minute slots');
  }
  
  const today = new Date();
  // Generate dates for the next 8 days
  const allDates = [];
  for (let i = 0; i < 8; i++) {
    allDates.push(addDays(today, i));
  }
  
  // Filter out holidays and closed days
  const availableDates = selectedShopForTimeSlots && shopSettings
    ? allDates.filter(date => isDateAvailable(date))
    : allDates;
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Available Time Slots
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => handleOpenNewBookingDialog()}
          disabled={!selectedShopForTimeSlots}
          startIcon={<Add />}
        >
          Schedule New Booking
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        View availability for the next 7 days, and click an open slot to book it. Orange slots are partially booked; gray slots are full.
      </Typography>
      
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="shop-select-label">Select Shop</InputLabel>
        <Select
          labelId="shop-select-label"
          value={selectedShopForTimeSlots}
          label="Select Shop"
          onChange={(e) => setSelectedShopForTimeSlots(e.target.value)}
        >
          <MenuItem value="">
            <em>Select a shop</em>
          </MenuItem>
          {shopIds.map(shopId => {
            const shop = shops.find(s => s.id === shopId);
            return (
              <MenuItem key={shopId} value={shopId}>
                {shop ? shop.name : `Shop ${shopId}`}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      
      {!selectedShopForTimeSlots ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please select a shop to view available time slots.
        </Alert>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            {availableDates.map((date) => {
              const isAvailable = isDateAvailable(date);
              const isSelected = format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
              
              // Get the reason for unavailability (for tooltip)
              let unavailableReason = '';
              if (!isAvailable) {
                if (isHoliday(date)) {
                  const holiday = shopSettings?.holidays.find(h => h.date === format(date, 'yyyy-MM-dd'));
                  unavailableReason = holiday ? `Holiday: ${holiday.description}` : 'Holiday';
                } else if (isClosedDay(date)) {
                  unavailableReason = 'Closed day';
                } else {
                  const specialHours = getSpecialHours(date);
                  if (specialHours && (!specialHours.open || !specialHours.close)) {
                    unavailableReason = `Special hours: ${specialHours.description}`;
                  }
                }
              }
              
              return (
                <Tooltip 
                  key={format(date, 'yyyy-MM-dd')} 
                  title={!isAvailable ? unavailableReason : ''}
                >
                  <span>
                    <Chip
                      label={format(date, 'EEE, MMM d')}
                      onClick={() => isAvailable && handleDateChange(date)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      disabled={!isAvailable}
                      sx={{
                        opacity: isAvailable ? 1 : 0.6,
                        cursor: isAvailable ? 'pointer' : 'not-allowed'
                      }}
                    />
                  </span>
                </Tooltip>
              );
            })}
          </Box>
          
          <Typography variant="subtitle1">
            Time slots for {format(selectedDate, 'EEEE, MMMM d, yyyy')}:
          </Typography>
          
          {isLoadingShopSlots || loadingSettings ? (
            <Skeleton variant="rectangular" height={100} sx={{ mt: 2 }} />
          ) : (
            <>
              {/* Slot Status Legend */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label="Available"
                    color="success"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Available
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label="Partial"
                    color="warning"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Partially Booked
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label="Booked"
                    disabled
                    size="small"
                    sx={{ 
                      mr: 1, 
                      opacity: 0.8,
                      backgroundColor: 'rgba(75, 75, 75, 0.25)',
                      border: '1px solid rgba(120, 120, 120, 0.5)',
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: '100%',
                        height: '1px',
                        backgroundColor: 'rgba(180, 180, 180, 0.8)',
                        left: 0,
                        top: '50%',
                      },
                      '& .MuiChip-label': {
                        color: 'rgba(200, 200, 200, 0.9)',
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Fully Booked (Max: {shopSettings?.maxConcurrentBookings || 1})
                  </Typography>
                </Box>
              </Box>
              
              {DEFAULT_TIME_SLOTS.length === 0 && availableTimeSlots.length === 0 ? (
                <Alert severity="info">
                  Loading time slots...
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {availableTimeSlots.map((slot) => {
                    // Get booking count for this slot using standard formatting
                    const bookingCount = slotBookingCounts[slot] || 0;
                    const maxBookings = shopSettings?.maxConcurrentBookings || 1;
                    const isFullyBooked = bookingCount >= maxBookings;
                    const isPartiallyBooked = bookingCount > 0 && bookingCount < maxBookings;
                    
                    return (
                      <Tooltip 
                        key={slot}
                        describeChild
                        title={
                          isFullyBooked 
                            ? `Fully booked (${bookingCount}/${maxBookings})` 
                            : isPartiallyBooked
                              ? `Book this time (${bookingCount}/${maxBookings} taken)`
                              : 'Book this time'
                        }
                      >
                        <Chip
                          label={formatTimeForDisplay(slot)}
                          data-slot-status={isFullyBooked ? 'full' : isPartiallyBooked ? 'partial' : 'available'}
                          clickable={!isFullyBooked}
                          onClick={isFullyBooked ? undefined : () => handleOpenNewBookingDialog(slot)}
                          color={isPartiallyBooked ? 'warning' : isFullyBooked ? 'default' : 'success'}
                          sx={{ 
                            m: 0.5,
                            opacity: isFullyBooked ? 0.8 : 1,
                            backgroundColor: isFullyBooked ? 'rgba(75, 75, 75, 0.25)' : undefined,
                            color: isFullyBooked ? '#aaa' : undefined,
                            borderColor: isFullyBooked ? 'rgba(120, 120, 120, 0.5)' : undefined,
                            border: isFullyBooked ? '1px solid rgba(120, 120, 120, 0.5)' : undefined,
                            position: 'relative',
                            '&::after': isFullyBooked ? {
                              content: '""',
                              position: 'absolute',
                              top: '50%',
                              left: 0,
                              width: '100%',
                              height: '1px',
                              backgroundColor: 'rgba(180, 180, 180, 0.8)',
                            } : {},
                            '& .MuiChip-label': {
                              color: isFullyBooked ? 'rgba(200, 200, 200, 0.9)' : undefined
                            }
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              )}
            </>
          )}
          
          {shopBookedSlots.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Booked Appointments:
              </Typography>
              {shopAppointments.map(appointment => (
                <Box 
                  key={appointment.id} 
                  sx={{ 
                    mb: 1, 
                    p: 2, 
                    border: 1, 
                    borderColor: 'divider',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Box>
                    <Typography variant="body1">
                      {formatAppointmentDateTime(appointment)} - {appointment.customerName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {appointment.vehicleModel} - {appointment.serviceDescription.substring(0, 50)}
                      {appointment.serviceDescription.length > 50 ? '...' : ''}
                    </Typography>
                  </Box>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={() => handleRescheduleClick(appointment)}
                  >
                    Reschedule
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default TimeSlotsTab;
